import { useEffect, useState } from "react";
import { api, fmtPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Bell, Plus, Zap } from "lucide-react";

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [code, setCode] = useState("WHEAT");
  const [condition, setCondition] = useState("above");
  const [threshold, setThreshold] = useState("");
  const [note, setNote] = useState("");

  const load = async () => {
    const [a, c] = await Promise.all([api.get("/alerts"), api.get("/commodities")]);
    setAlerts(a.data); setCommodities(c.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/alerts", { commodity_code: code, condition, threshold: parseFloat(threshold), note });
      toast.success("Alert created");
      setThreshold(""); setNote(""); await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/alerts/${id}`);
    toast.success("Deleted");
    await load();
  };

  const forceFire = async (id) => {
    try {
      const { data } = await api.post(`/alerts/${id}/force-fire`);
      const emailStatus = data.email?.sent ? "sent" : data.email?.queued ? "queued (Resend not configured)" : "failed";
      toast.success(`Alert fired · email ${emailStatus}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-primary">Alerts</div>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Custom price alerts</h1>
        <p className="mt-1 text-sm text-muted-foreground">Set threshold, price-move and volatility alerts. Notifications simulated in MVP.</p>
      </div>

      <Card className="border-border/60 bg-card/60">
        <CardHeader><CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground">New alert</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={create} className="grid gap-3 sm:grid-cols-[1.2fr_1fr_1fr_1.4fr_auto]">
            <Select value={code} onValueChange={setCode}>
              <SelectTrigger data-testid="alert-commodity"><SelectValue /></SelectTrigger>
              <SelectContent>
                {commodities.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={condition} onValueChange={setCondition}>
              <SelectTrigger data-testid="alert-condition"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="above">Price above</SelectItem>
                <SelectItem value="below">Price below</SelectItem>
                <SelectItem value="pct_change">% change ≥</SelectItem>
              </SelectContent>
            </Select>
            <Input type="number" step="0.0001" placeholder="Threshold" data-testid="alert-threshold" value={threshold} onChange={e => setThreshold(e.target.value)} required />
            <Input placeholder="Note (optional)" data-testid="alert-note" value={note} onChange={e => setNote(e.target.value)} />
            <Button type="submit" data-testid="alert-submit" className="rounded-full"><Plus className="mr-1 h-4 w-4" />Create</Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-border/60 bg-card/60">
        <CardHeader><CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground">Your alerts</CardTitle></CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <div className="rounded-md border border-dashed border-border/60 p-8 text-center text-sm text-muted-foreground">
              <Bell className="mx-auto h-6 w-6 text-muted-foreground" /><div className="mt-2">No alerts yet.</div>
            </div>
          ) : (
            <div className="divide-y divide-border/60">
              {alerts.map(a => (
                <div key={a.id} data-testid={`alert-row-${a.id}`} className="flex items-center justify-between py-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="uppercase text-[10px]">{a.commodity_code}</Badge>
                      <span className="text-sm font-semibold">
                        {a.condition === "above" && "Price above "}
                        {a.condition === "below" && "Price below "}
                        {a.condition === "pct_change" && "% change ≥ "}
                        <span className="num">{fmtPrice(a.threshold, 4)}</span>
                      </span>
                    </div>
                    {a.note && <div className="mt-1 text-xs text-muted-foreground">{a.note}</div>}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" data-testid={`alert-fire-${a.id}`} onClick={() => forceFire(a.id)} title="Simulate this alert firing now (creates a notification + email)">
                      <Zap className="h-4 w-4 text-[hsl(var(--signal-hold))]" />
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`alert-delete-${a.id}`} onClick={() => remove(a.id)}>
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
